import { EChartsOption } from 'echarts';
import ReactECharts from 'echarts-for-react';
import { merge } from 'lodash';

type PieChartProps = {
  data: { name: string; value: number }[];
  hasLegend?: boolean;
  colors?: string[];
  hasDataLabel?: boolean;
  options?: EChartsOption;
  hasEnabledExporting?: boolean;
  hasTooltip?: boolean;
  chartTitle?: string;
  chartSize?: string;
  onClick?: () => void;
  subTitle?: string;
  dataTestId?: string;
  chartHeight?: string | number;
  chartWidth?: string | number;
  marginLeft?: number;
};

const PieChart = ({
  data,
  hasLegend,
  colors,
  hasDataLabel,
  options,
  hasEnabledExporting,
  hasTooltip = true,
  chartTitle,
  chartSize = '50%',
  onClick,
  subTitle,
  dataTestId,
  chartHeight = '100%',
  chartWidth = '100%',
  marginLeft,
}: PieChartProps): React.ReactElement => {
  const getChartOptions = (): EChartsOption => {
    const defaultOptions: EChartsOption = {
      color: colors,
      title: {
        text: chartTitle,
        subtext: subTitle,
        subtextStyle: {
          color: '#2c24c3',
          fontSize: '2rem',
        },
        left: 'center',
        show: true,
      },
      series: [
        {
          type: 'pie',
          name: chartTitle,
          data,
          radius: chartSize,
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)',
            },
          },
          label: {
            show: hasDataLabel,
          },
          labelLine: {
            show: hasDataLabel,
          },
          itemStyle: {
            borderColor: '#fff',
            borderWidth: 0,
          },
        },
      ],
      legend: {
        show: hasLegend,
        orient: 'vertical',
        left: 'left',
        bottom: 0,
      },
      tooltip: {
        show: hasTooltip,
        trigger: 'item',
      },
      toolbox: {
        show: hasEnabledExporting,
        showTitle: false,
        right: 0,
        top: 0,
        itemSize: 10,
        feature: {
          saveAsImage: {
            type: 'png',
            show: true,
            iconStyle: {
              borderColor: '#131221',
            },
            emphasis: {
              iconStyle: {
                borderColor: '#7d8be6',
              },
            },
          },
          dataZoom: {
            show: false,
          },
          restore: {
            show: false,
          },
          dataView: {
            show: false,
          },
          magicType: {
            show: false,
          },
          brush: {
            show: false,
          },
        },
      },
      textStyle: {
        fontFamily: 'Inter',
      },
      backgroundColor: '#F8F8F9',
    };

    return merge({}, defaultOptions, options);
  };

  return (
    <div role="presentation" onClick={onClick} data-test-id={`${dataTestId}-click-wrapper`}>
      <ReactECharts
        data-test-id={dataTestId}
        option={getChartOptions()}
        style={{ height: chartHeight, marginLeft, width: chartWidth }}
      />
    </div>
  );
};

export default PieChart;
